import I18n from 'I18n';
import { Select } from 'foundations-components/transitional/components/Select';
import UsersAndTeamsSearchOptionComponent from './UsersAndTeamsSearchOptionComponent';
import { isTeamId, isUserId, makeTeamIdForSelect, makeUserIdForSelect, extractTeamIdFromSelect, extractUserIdFromSelect } from './UsersAndTeamsSearchUtils';
import { getExplicitlySelectedUserIds, getSortedFlattenedTeamsList } from '../../utils/TeamUtils';
import { getSortedUsers } from '../../utils/UserUtils';
import { makeUserSelectOption } from './UserSelectOption';
import { makeFlatListOfTeamSelectOptions } from './TeamSelectOptions';
import usePaginatedUsers from '../../hooks/usePaginatedUsers';
import useBulkFetchUsers from '../../hooks/useBulkFetchUsers';
import { jsx as _jsx } from "react/jsx-runtime";
// Takes a list of IDs [team-1, user-2] ect. and
// returns two seperate lists with unique key for users and teams
const getSelectedUserAndTeamIdsFromCombinedList = values => {
  const result = values.reduce((acc, id) => {
    if (isTeamId(id)) {
      acc.selectedTeamIds.push(extractTeamIdFromSelect(id));
    } else if (isUserId(id)) {
      acc.selectedUserIds.push(extractUserIdFromSelect(id));
    }
    return acc;
  }, {
    selectedUserIds: [],
    selectedTeamIds: []
  });
  return result;
};
const getExplicitlySelectedUsers = ({
  selectedUserIds,
  userIdsForSelectedTeamIds
}) => {
  return selectedUserIds.filter(userId => !userIdsForSelectedTeamIds.includes(userId));
};

// Takes three lists of ID's, users, teams and users from selected teams, and creates one list
// of ID's with a unique prefix in front of users/teams to distinguish
// which type of ID the selected option is referring to.
const makeCombinedUserAndTeamIdsList = ({
  selectedUserIds,
  selectedTeamIds,
  userIdsForSelectedTeamIds,
  teams
}) => {
  const explicitlySelectedUsers = getExplicitlySelectedUsers({
    selectedUserIds,
    userIdsForSelectedTeamIds
  });
  const existingSelectedTeams = getSortedFlattenedTeamsList(teams).filter(({
    id
  }) => selectedTeamIds.includes(id));
  return explicitlySelectedUsers.map(id => makeUserIdForSelect(id)).concat(existingSelectedTeams.map(team => makeTeamIdForSelect(team.id)));
};
const makeUserAndTeamGroupedSelectOptions = (users, teams, userIdsForSelectedTeamIds, disabledUsers, allowedUserIds, allowedTeamIds) => {
  const sortedUsers = getSortedUsers(users);
  const userSelectOptions = sortedUsers.map(user => makeUserSelectOption(user, userIdsForSelectedTeamIds, disabledUsers, allowedUserIds));
  const teamSelectOptions = makeFlatListOfTeamSelectOptions({
    teams,
    allowedTeamIds
  });
  return [{
    text: I18n.text('ui-asset-management-lib.labels.teams'),
    options: teamSelectOptions
  }, {
    text: I18n.text('ui-asset-management-lib.labels.users'),
    options: userSelectOptions
  }];
};

// For two identical lists, with one unique value, find the unique value between the two
const getUniqueId = (listOne, listTwo) => {
  if (listOne.length > listTwo.length) {
    return listOne.find(id => !listTwo.includes(id));
  }
  return listTwo.find(id => !listOne.includes(id));
};
const UsersAndTeamsSearch = ({
  teams,
  selectedUserIds,
  selectedTeamIds,
  userIdsForSelectedTeamIds,
  onAddTeam,
  onRemoveTeam,
  onAddUser,
  onRemoveUser,
  allowedTeamIds,
  DisallowedTeamFormattedMessage,
  disabledUsers,
  allowedUserIds,
  disallowedUserTooltip,
  isTeamsOnly
}) => {
  const {
    users: selectedUsers
  } = useBulkFetchUsers(Array.from(new Set([...selectedUserIds, ...userIdsForSelectedTeamIds])));
  const {
    loadOptions
  } = usePaginatedUsers(100, users => {
    return makeUserAndTeamGroupedSelectOptions(isTeamsOnly ? [] : users, teams, userIdsForSelectedTeamIds, disabledUsers, allowedUserIds, allowedTeamIds);
  });
  const selectedUsersAndTeamIds = makeCombinedUserAndTeamIdsList({
    selectedUserIds,
    selectedTeamIds,
    userIdsForSelectedTeamIds,
    teams
  });
  const selectedUsersOptions = makeUserAndTeamGroupedSelectOptions(selectedUsers, teams, userIdsForSelectedTeamIds, disabledUsers, allowedUserIds, allowedTeamIds);
  const handleChange = ({
    target: {
      value
    }
  }) => {
    const newSelectedOptions = getSelectedUserAndTeamIdsFromCombinedList(value);
    const newSelectedUserIds = getExplicitlySelectedUserIds(newSelectedOptions.selectedUserIds, selectedTeamIds, teams);
    const oldSelectedUserIds = getExplicitlySelectedUserIds(selectedUserIds, selectedTeamIds, teams);
    const didAddTeam = newSelectedOptions.selectedTeamIds.length > selectedTeamIds.length;
    const didRemoveTeam = newSelectedOptions.selectedTeamIds.length < selectedTeamIds.length;
    const didAddUser = newSelectedUserIds.length > oldSelectedUserIds.length;
    const didRemoveUser = newSelectedUserIds.length < oldSelectedUserIds.length;
    if (didAddTeam) {
      onAddTeam(getUniqueId(newSelectedOptions.selectedTeamIds, selectedTeamIds));
    } else if (didRemoveTeam) {
      onRemoveTeam(getUniqueId(newSelectedOptions.selectedTeamIds, selectedTeamIds));
    } else if (didAddUser) {
      onAddUser(getUniqueId(newSelectedUserIds, oldSelectedUserIds));
    } else if (didRemoveUser) {
      onRemoveUser(getUniqueId(newSelectedUserIds, oldSelectedUserIds));
    }
  };
  const renderTeamOption = data => {
    return /*#__PURE__*/_jsx(UsersAndTeamsSearchOptionComponent, Object.assign({}, data, {
      DisallowedTeamFormattedMessage: DisallowedTeamFormattedMessage,
      disallowedUserTooltip: disallowedUserTooltip
    }));
  };
  return /*#__PURE__*/_jsx(Select, {
    "data-test-id": "users-and-team-search-input",
    multi: true,
    anchorType: "button",
    value: selectedUsersAndTeamIds,
    optionComponent: renderTeamOption,
    onChange: handleChange,
    loadOptions: loadOptions
    // This loads pre-selected users that are not already loaded from "loadOptions"
    ,
    options: selectedUsersOptions,
    multiCollapseLimit: 5
  });
};
export default UsersAndTeamsSearch;